import { useState } from "react";
import { useLang } from "@/context/LanguageContext";

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const fmtKey = (k) => String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function fmtVal(v, t, loc) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString(loc, { maximumFractionDigits: 4 });
  if (typeof v === "boolean") return v ? t("dr.yes") : t("dr.no");
  return String(v);
}

function Scalar({ value, t, loc }) {
  const isNum = typeof value === "number";
  const cls = isNum && value > 0 ? "text-emerald-400" : isNum && value < 0 ? "text-red-400" : "text-slate-200";
  return <span className={`font-mono text-sm ${cls}`}>{fmtVal(value, t, loc)}</span>;
}

function Table({ rows, t, loc }) {
  const [expanded, setExpanded] = useState(false);
  const cols = Array.from(
    rows.slice(0, 30).reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => set.add(k));
      return set;
    }, new Set())
  ).slice(0, 8);
  const shown = expanded ? rows : rows.slice(0, 12);
  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-md border border-slate-800">
        <table className="w-full text-left" data-testid="data-table">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/40">
              {cols.map((c) => (
                <th key={c} className="whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{fmtKey(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i} className="border-b border-slate-800 transition-colors last:border-0 hover:bg-slate-800/40">
                {cols.map((c) => {
                  const v = r?.[c];
                  const complex = isObj(v) || Array.isArray(v);
                  return (
                    <td key={c} className="px-3 py-2 align-top">
                      {complex ? (
                        <span className="font-mono text-xs text-slate-400">{JSON.stringify(v).slice(0, 60)}</span>
                      ) : (
                        <Scalar value={v} t={t} loc={loc} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 12 && (
        <button onClick={() => setExpanded((e) => !e)} className="mt-2 text-xs text-blue-400 hover:text-blue-300" data-testid="table-expand-btn">
          {expanded ? t("dr.collapse") : t("dr.viewRows", { n: rows.length })}
        </button>
      )}
    </div>
  );
}

function renderNode(data, t, loc, depth = 0) {
  if (data === null || data === undefined) return <p className="text-sm text-slate-500">{t("dr.none")}</p>;
  if (Array.isArray(data)) {
    if (data.length === 0) return <p className="text-sm text-slate-500">{t("dr.none")}</p>;
    if (isObj(data[0])) return <Table rows={data} t={t} loc={loc} />;
    return (
      <ul className="space-y-1">
        {data.map((v, i) => <li key={i} className="text-sm text-slate-300">• {fmtVal(v, t, loc)}</li>)}
      </ul>
    );
  }
  if (isObj(data)) {
    const entries = Object.entries(data);
    const scalars = entries.filter(([, v]) => !isObj(v) && !Array.isArray(v));
    const complex = entries.filter(([, v]) => isObj(v) || Array.isArray(v));
    return (
      <div className="space-y-4">
        {scalars.length > 0 && (
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {scalars.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3 border-b border-slate-800/60 py-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{fmtKey(k)}</span>
                <Scalar value={v} t={t} loc={loc} />
              </div>
            ))}
          </div>
        )}
        {depth < 4 && complex.map(([k, v]) => (
          <div key={k}>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{fmtKey(k)}</div>
            {renderNode(v, t, loc, depth + 1)}
          </div>
        ))}
      </div>
    );
  }
  return <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{fmtVal(data, t, loc)}</p>;
}

export function DataRenderer({ data }) {
  const { t, lang } = useLang();
  const loc = lang === "en" ? "en-US" : "fr-FR";
  return renderNode(data, t, loc, 0);
}
